import React, { useEffect, useState, lazy, createElement } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { Upload, CheckCircle, FileText, Shield, Users, Target, X, AlertCircle, Send, Search, UserCheck, Handshake } from 'lucide-react';
const MAX_FILE_SIZE = 48 * 1024 * 1024;
import { submitForm } from '../utils/submitForm';
import { CVFormModal } from '../components/CVFormModal';
import { CompanyFormModal } from '../components/CompanyFormModal';
export function SpecialistsPage() {
  const [dragActive, setDragActive] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedRank, setSelectedRank] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const {
    t,
    language
  } = useLanguage();
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [otherRank, setOtherRank] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [yearsExp, setYearsExp] = useState('');
  const [availability, setAvailability] = useState('');
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  // Hero modal state
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  // SEO: Set document title, meta description, and FAQ structured data
  useEffect(() => {
    document.title = t('specialists.seo.title');
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t('specialists.seo.description'));
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;
    // Open Graph tags
    const ogTags: Record<string, string> = {
      'og:title': t('specialists.seo.title'),
      'og:description': t('specialists.seo.description'),
      'og:type': 'website',
      'og:url': window.location.origin + window.location.pathname,
      'og:site_name': 'Update SEO Meta Tags All Pages'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    // Twitter Card tags
    const twitterTags: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': t('specialists.seo.title'),
      'twitter:description': t('specialists.seo.description')
    };
    Object.entries(twitterTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    // Preload this page's hero image
    let heroPreload = document.getElementById('page-hero-preload') as HTMLLinkElement;
    if (!heroPreload) {
      heroPreload = document.createElement('link') as HTMLLinkElement;
      heroPreload.id = 'page-hero-preload';
      heroPreload.rel = 'preload';
      heroPreload.as = 'image';
      document.head.appendChild(heroPreload);
    }
    heroPreload.href = 'https://cdn.magicpatterns.com/uploads/o1c9rQhNQZG9dHqBoXXqmj/sunrise-busy-industrial-port-with-ships.jpg';
    // FAQPage structured data (JSON-LD)
    const faqItems = t('specialists.faq.items');
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item: any) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    };
    let scriptTag = document.getElementById('specialists-faq-structured-data');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'specialists-faq-structured-data';
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(faqSchema);
    return () => {
      const existingScript = document.getElementById('specialists-faq-structured-data');
      if (existingScript) existingScript.remove();
      const preload = document.getElementById('page-hero-preload');
      if (preload) preload.remove();
    };
  }, [t]);
  // Data from translations
  const benefits = [{
    icon: FileText,
    ...t('specialists.benefits.items.profile')
  }, {
    icon: Shield,
    ...t('specialists.benefits.items.docs')
  }, {
    icon: Users,
    ...t('specialists.benefits.items.matching')
  }, {
    icon: Target,
    ...t('specialists.benefits.items.access')
  }];
  const steps = [{
    number: '01',
    icon: Send,
    ...t('specialists.steps.items')[0]
  }, {
    number: '02',
    icon: Search,
    ...t('specialists.steps.items')[1]
  }, {
    number: '03',
    icon: UserCheck,
    ...t('specialists.steps.items')[2]
  }, {
    number: '04',
    icon: Handshake,
    ...t('specialists.steps.items')[3]
  }];
  const requirements = t('specialists.requirements.items');
  const faqs = t('specialists.faq.items');
  const rankOptions = [{
    category: t('specialists.form.ranks.deck'),
    options: [t('specialists.form.ranks.options.master'), t('specialists.form.ranks.options.chiefMate'), t('specialists.form.ranks.options.secondMate'), t('specialists.form.ranks.options.thirdMate'), t('specialists.form.ranks.options.bosun'), t('specialists.form.ranks.options.ab'), t('specialists.form.ranks.options.os')]
  }, {
    category: t('specialists.form.ranks.engine'),
    options: [t('specialists.form.ranks.options.chiefEng'), t('specialists.form.ranks.options.secondEng'), t('specialists.form.ranks.options.thirdEng'), t('specialists.form.ranks.options.fourthEng'), t('specialists.form.ranks.options.eto'), t('specialists.form.ranks.options.asstEto'), t('specialists.form.ranks.options.motorman'), t('specialists.form.ranks.options.wiper')]
  }, {
    category: t('specialists.form.ranks.other'),
    options: [t('specialists.form.ranks.options.dpo'), t('specialists.form.ranks.options.sdpo'), t('specialists.form.ranks.options.craneOp'), t('specialists.form.ranks.options.rigger'), t('specialists.form.ranks.options.electrician'), t('specialists.form.ranks.options.mechanic'), t('specialists.form.ranks.options.other')]
  }];
  const vesselTypes = [{
    value: 'tanker',
    label: t('specialists.form.vessels.tanker')
  }, {
    value: 'bulk',
    label: t('specialists.form.vessels.bulk')
  }, {
    value: 'container',
    label: t('specialists.form.vessels.container')
  }, {
    value: 'offshore',
    label: t('specialists.form.vessels.offshore')
  }, {
    value: 'cruise',
    label: t('specialists.form.vessels.cruise')
  }, {
    value: 'other',
    label: t('specialists.form.vessels.other')
  }];
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const validateFile = (file: File): boolean => {
    setFileError('');
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 48 MB limit');
      return false;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Invalid file type. Please upload PDF, DOC, DOCX, or image files (PNG, JPG, GIF, WebP)');
      return false;
    }
    return true;
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const removeFile = () => {
    setUploadedFile(null);
    setFileError('');
  };
  const handleRankChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedRank(value);
    setShowOtherInput(value === t('specialists.form.ranks.options.other'));
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setSelectedRank('');
    setOtherRank('');
    setShowOtherInput(false);
    setEmail('');
    setPhone('');
    setVesselType('');
    setYearsExp('');
    setAvailability('');
    setAgreedToTerms(false);
    setUploadedFile(null);
    setFileError('');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms || submitting) return;
    // Validate required file upload
    if (!uploadedFile) {
      setFileError(t('specialists.form.labels.cvRequired') || 'Please upload your CV');
      return;
    }
    setSubmitting(true);
    setSubmitStatus('idle');
    const finalRank = showOtherInput ? otherRank : selectedRank;
    const vesselLabel = vesselTypes.find((v: any) => v.value === vesselType)?.label || vesselType;
    const messageParts = [`Rank: ${finalRank}`];
    if (vesselLabel) messageParts.push(`Vessel exp: ${vesselLabel}`);
    if (yearsExp) messageParts.push(`Years: ${yearsExp}`);
    if (availability) messageParts.push(`Available: ${availability}`);
    const fd = new FormData();
    fd.append('name', `${firstName} ${lastName}`.trim());
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', messageParts.join(' | '));
    if (uploadedFile) {
      fd.append('file', uploadedFile, uploadedFile.name);
      fd.append('document', uploadedFile, uploadedFile.name);
      fd.append('cv', uploadedFile, uploadedFile.name);
    }
    try {
      const result = await submitForm('resume', fd, language);
      if (result.ok) {
        setSubmitStatus('success');
        resetForm();
        setTimeout(() => setSubmitStatus('idle'), 4000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-1228">
      <Header data-id="element-1229" />

      {/* Hero Section */}
      <section className="w-full px-6 md:px-12 py-8" data-id="element-1230">
        <div className="max-w-7xl mx-auto" data-id="element-1231">
          <div className="hidden md:block relative w-full rounded-2xl overflow-hidden" data-id="element-1232">
            <img src="https://cdn.magicpatterns.com/uploads/o1c9rQhNQZG9dHqBoXXqmj/sunrise-busy-industrial-port-with-ships.jpg" alt="Industrial port at sunrise" className="w-full h-[600px] lg:h-[700px] object-cover object-center" fetchPriority="high" decoding="async" width={1400} height={700} sizes="(min-width: 1280px) 1200px, 100vw" data-id="element-1233" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-1234" />
            <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 pb-20 lg:pb-24" data-id="element-1235">
              <div className="max-w-2xl" data-id="element-1236">
                <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight drop-shadow-lg" data-id="element-1237">
                  {t('specialists.hero.title')}
                </h1>
                <p className="text-white/95 text-base lg:text-lg leading-relaxed mb-6 drop-shadow-md" data-id="element-1238">
                  {t('specialists.hero.desc')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-1239">
                  <button onClick={() => setCompanyModalOpen(true)} className="px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity shadow-lg inline-flex items-center justify-center gap-2" data-id="element-1240">
                    {t('hero.discussTask')}
                  </button>
                  <button onClick={() => setCvModalOpen(true)} className="px-6 py-3 bg-white/95 text-[var(--color-dark)] text-sm font-medium rounded-md hover:bg-white transition-colors shadow-lg inline-flex items-center justify-center gap-2" data-id="element-1241">
                    <Upload className="w-4 h-4" data-id="element-1242" />
                    {t('hero.submitProfile')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="md:hidden space-y-6 pb-20" data-id="element-1243">
            <h1 className="text-3xl font-bold text-[var(--color-dark)] leading-tight text-center" data-id="element-1244">
              {t('specialists.hero.title')}
            </h1>
            <div className="relative w-full rounded-2xl overflow-hidden" data-id="element-1245">
              <img src="https://cdn.magicpatterns.com/uploads/o1c9rQhNQZG9dHqBoXXqmj/sunrise-busy-industrial-port-with-ships.jpg" alt="Industrial port" className="w-full h-[300px] object-cover" fetchPriority="high" decoding="async" width={800} height={300} sizes="100vw" style={{
              objectPosition: '75% 50%'
            }} data-id="element-1246" />
            </div>
            <p className="text-[var(--color-dark)]/80 text-sm leading-relaxed text-center" data-id="element-1247">
              {t('specialists.hero.desc')}
            </p>
            <div className="flex flex-col gap-3" data-id="element-1248">
              <button onClick={() => setCompanyModalOpen(true)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2" data-id="element-1249">
                {t('hero.discussTask')}
              </button>
              <button onClick={() => setCvModalOpen(true)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2" data-id="element-1250">
                <Upload className="w-4 h-4" data-id="element-1251" />
                {t('hero.submitProfile')}
              </button>
            </div>
          </div>

          <div className="text-center mt-6 space-y-2" data-id="element-1252">
            <p className="text-[var(--color-text-light)] text-sm" data-id="element-1253">
              {t('specialists.hero.tagline1')}
            </p>
            <p className="text-[var(--color-text-light)] text-sm" data-id="element-1254">
              {t('specialists.hero.tagline2')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <div className="cv-lazy-section" data-id="element-1255">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-1256">
          <div className="max-w-7xl mx-auto" data-id="element-1257">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-8" data-id="element-1258">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1259">
                {t('specialists.benefits.pill')}
              </span>
            </div>

            <div className="flex flex-col lg:flex-row gap-12" data-id="element-1260">
              <div className="lg:w-2/5" data-id="element-1261">
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-6 leading-tight whitespace-pre-line" data-id="element-1262">
                  {t('specialists.benefits.title')}
                </h2>
                <p className="text-[var(--color-text-light)] text-sm leading-relaxed mb-4" data-id="element-1263">
                  {t('specialists.benefits.desc1')}
                </p>
                <p className="text-[var(--color-text-light)] text-sm leading-relaxed" data-id="element-1264">
                  {t('specialists.benefits.desc2')}
                </p>
              </div>

              <div className="lg:w-3/5" data-id="element-1265">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1266">
                  {benefits.map((benefit, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl" data-id="element-1267">
                      <benefit.icon className="w-6 h-6 text-[var(--color-tan)] mb-4" strokeWidth={1.5} data-id="element-1268" />
                      <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-1269">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-[var(--color-text-light)]" data-id="element-1270">
                        {benefit.desc}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* How It Works - Zigzag with Cards */}
      <div className="cv-lazy-section" data-id="element-1271">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-1272">
          <div className="max-w-5xl mx-auto" data-id="element-1273">
            <div className="text-center mb-10" data-id="element-1274">
              <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-5 bg-white" data-id="element-1275">
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1276">
                  {t('specialists.steps.pill')}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-3" data-id="element-1277">
                {t('specialists.steps.title')}
              </h2>
              <p className="text-base text-gray-600 max-w-xl mx-auto" data-id="element-1278">
                {t('specialists.steps.desc')}
              </p>
            </div>

            {/* Desktop: Zigzag Cards with Connecting Lines */}
            <div className="hidden md:block relative" data-id="element-1279">
              {/* Main vertical center line */}
              <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gray-200" data-id="element-1280" />

              <div className="space-y-5 relative" data-id="element-1281">
                {steps.map((step, index) => {
                const isRight = index % 2 === 0;
                const StepIcon = step.icon;
                return <div key={index} className="relative" data-id="element-1282">
                      {/* Horizontal branch line */}
                      <div className={`absolute top-1/2 -translate-y-1/2 h-px bg-gray-200 ${isRight ? 'left-1/2 w-[8%]' : 'right-1/2 w-[8%]'}`} data-id="element-1283" />

                      {/* Center dot */}
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--color-tan)] z-10" data-id="element-1284" />

                      {/* Card */}
                      <div className={`flex ${isRight ? 'justify-end' : 'justify-start'}`} data-id="element-1285">
                        <div className="w-[42%]" data-id="element-1286">
                          <div className="bg-white border border-gray-200 rounded-xl p-5 relative" data-id="element-1287">
                            <span className="absolute top-3 right-4 text-xs font-medium text-gray-400" data-id="element-1288">
                              {step.number}
                            </span>
                            <div className="flex items-center gap-4 pr-10" data-id="element-1289">
                              <div className="w-10 h-10 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-1290">
                                <StepIcon className="w-5 h-5 text-[var(--color-tan)]" data-id="element-1291" />
                              </div>
                              <div className="flex-1" data-id="element-1292">
                                <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-1293">
                                  {step.title}
                                </h3>
                                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1294">
                                  {step.desc}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Mobile: Vertical cards with connecting line */}
            <div className="md:hidden relative" data-id="element-1295">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" data-id="element-1296" />

              <div className="space-y-4" data-id="element-1297">
                {steps.map((step, index) => {
                const StepIcon = step.icon;
                return <div key={index} className="relative flex gap-4" data-id="element-1298">
                      <div className="w-8 flex-shrink-0 flex justify-center relative z-10" data-id="element-1299">
                        <div className="w-2 h-2 rounded-full bg-[var(--color-tan)] mt-6" data-id="element-1300" />
                      </div>
                      <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 relative" data-id="element-1301">
                        <span className="absolute top-3 right-3 text-xs font-medium text-gray-400" data-id="element-1302">
                          {step.number}
                        </span>
                        <div className="flex items-center gap-3 pr-8" data-id="element-1303">
                          <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-1304">
                            <StepIcon className="w-4 h-4 text-[var(--color-tan)]" data-id="element-1305" />
                          </div>
                          <div className="flex-1" data-id="element-1306">
                            <h3 className="text-sm font-semibold text-[var(--color-text-dark)] mb-0.5" data-id="element-1307">
                              {step.title}
                            </h3>
                            <p className="text-xs text-gray-500" data-id="element-1308">{step.desc}</p>
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* FAQ */}
      <div className="cv-lazy-section" data-id="element-1309">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-1310">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1311">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6" data-id="element-1312">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1313">
                {t('specialists.faq.pill')}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-12" data-id="element-1314">
              {t('specialists.faq.title')}
            </h2>

            <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-gray-200 text-left" data-id="element-1315">
              <div className="space-y-0" data-id="element-1316">
                {faqs.map((faq: any, index: number) => <div key={index} className="border-b border-gray-100 last:border-0" data-id="element-1317">
                    <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full py-5 flex items-start justify-between text-left gap-4" aria-expanded={openFaq === index} data-id="element-1318">
                      <h3 className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-1319">
                        {faq.q}
                      </h3>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === index ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-1320">
                        <svg className={`w-4 h-4 transition-colors ${openFaq === index ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1321">
                          {openFaq === index ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-1322" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-1323" />}
                        </svg>
                      </div>
                    </button>
                    {/* Answer always in DOM for SEO — visually hidden when collapsed */}
                    <div className={`overflow-hidden transition-all duration-200 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1324">
                      <p className="pb-5 text-sm text-[var(--color-text-light)]" data-id="element-1325">
                        {faq.a}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Requirements */}
      <div className="cv-lazy-section-sm" data-id="element-1326">
        <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-1327">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1328">
            <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-1329">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1330">
                {t('specialists.requirements.pill')}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-1331">
              {t('specialists.requirements.title')}
            </h2>
            <p className="text-lg text-[var(--color-text-light)] mb-8" data-id="element-1332">
              {t('specialists.requirements.desc')}
            </p>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 text-left" data-id="element-1333">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1334">
                {requirements.map((req: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-1335">
                    <CheckCircle className="w-5 h-5 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-1336" />
                    <span className="text-sm text-[var(--color-text-dark)]" data-id="element-1337">
                      {req}
                    </span>
                  </div>)}
              </div>
              <p className="text-xs text-[var(--color-text-light)] italic border-t border-gray-200 pt-4" data-id="element-1338">
                {t('specialists.requirements.note')}
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* CV Form */}
      <div className="cv-lazy-section" data-id="element-1339">
        <section id="cv-form" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-1340">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1341">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6" data-id="element-1342">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1343">
                {t('specialists.form.pill')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-1344">
              {t('specialists.form.title')}
            </h2>
            <p className="text-lg text-[var(--color-text-light)] mb-2" data-id="element-1345">
              {t('specialists.form.desc1')}
            </p>
            <p className="text-base text-[var(--color-text-light)] font-medium mb-8" data-id="element-1346">
              {t('specialists.form.desc2')}
            </p>

            <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 text-left" data-id="element-1347">
              <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1348">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1349">
                  <div data-id="element-1350">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1351">
                      {t('specialists.form.labels.firstName')}{' '}
                      <span className="text-red-500" data-id="element-1352">*</span>
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1353" />
                  </div>
                  <div data-id="element-1354">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1355">
                      {t('specialists.form.labels.lastName')}{' '}
                      <span className="text-red-500" data-id="element-1356">*</span>
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1357" />
                  </div>
                </div>

                <div data-id="element-1358">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1359">
                    {t('specialists.form.labels.rank')}{' '}
                    <span className="text-red-500" data-id="element-1360">*</span>
                  </label>
                  <select value={selectedRank} onChange={handleRankChange} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center'
                }} data-id="element-1361">
                    <option value="" data-id="element-1362">
                      {t('specialists.form.labels.selectRank')}
                    </option>
                    {rankOptions.map(group => <optgroup key={group.category} label={group.category} data-id="element-1363">
                        {group.options.map(option => <option key={option} value={option} data-id="element-1364">
                            {option}
                          </option>)}
                      </optgroup>)}
                  </select>
                </div>

                {showOtherInput && <div data-id="element-1365">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1366">
                      {t('specialists.form.labels.specify')}{' '}
                      <span className="text-red-500" data-id="element-1367">*</span>
                    </label>
                    <input type="text" value={otherRank} onChange={e => setOtherRank(e.target.value)} required placeholder={t('specialists.form.labels.enterPosition')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1368" />
                  </div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1369">
                  <div data-id="element-1370">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1371">
                      {t('specialists.form.labels.email')}{' '}
                      <span className="text-red-500" data-id="element-1372">*</span>
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1373" />
                  </div>
                  <div data-id="element-1374">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1375">
                      {t('specialists.form.labels.phone')}{' '}
                      <span className="text-red-500" data-id="element-1376">*</span>
                    </label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} required placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1377" />
                    <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-1378">
                      {t('specialists.form.labels.phoneHint')}
                    </p>
                  </div>
                </div>

                <div data-id="element-1379">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1380">
                    {t('specialists.form.labels.cv')}{' '}
                    <span className="text-red-500" data-id="element-1381">*</span>
                  </label>

                  {!uploadedFile ? <div data-id="element-1382">
                      <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragActive ? 'border-[var(--color-tan)] bg-[var(--color-tan)]/5' : 'border-gray-300 hover:border-gray-400'}`} onClick={() => document.getElementById('cv-upload')?.click()} data-id="element-1383">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-1384" />
                        <p className="text-sm text-[var(--color-text-dark)] mb-1" data-id="element-1385">
                          {t('specialists.form.labels.dragDrop')}
                        </p>
                        <p className="text-xs text-[var(--color-text-light)]" data-id="element-1386">
                          {t('specialists.form.labels.fileTypes')}
                        </p>
                        <input id="cv-upload" type="file" accept=".pdf,.doc,.docx,image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleFileChange} className="hidden" data-id="element-1387" />
                      </div>
                      {fileError && <div className="mt-2 flex items-start gap-2 text-red-500 text-sm" data-id="element-1388">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1389" />
                          <span data-id="element-1390">{fileError}</span>
                        </div>}
                    </div> : <div className="border-2 border-[var(--color-tan)] bg-[var(--color-tan)]/5 rounded-lg p-4 flex items-center justify-between" data-id="element-1391">
                      <div className="flex items-center gap-3" data-id="element-1392">
                        <FileText className="w-8 h-8 text-[var(--color-tan)]" data-id="element-1393" />
                        <div data-id="element-1394">
                          <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-1395">
                            {uploadedFile.name}
                          </p>
                          <p className="text-xs text-[var(--color-text-light)]" data-id="element-1396">
                            {formatFileSize(uploadedFile.size)}
                          </p>
                        </div>
                      </div>
                      <button type="button" onClick={removeFile} className="p-2 hover:bg-red-50 rounded-full transition-colors" data-id="element-1397">
                        <X className="w-5 h-5 text-red-500" data-id="element-1398" />
                      </button>
                    </div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1399">
                  <div data-id="element-1400">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1401">
                      {t('specialists.form.labels.vesselType')}{' '}
                      <span className="text-red-500" data-id="element-1402">*</span>
                    </label>
                    <select value={vesselType} onChange={e => setVesselType(e.target.value)} required className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center'
                  }} data-id="element-1403">
                      <option value="" data-id="element-1404">
                        {t('specialists.form.labels.selectVessel')}
                      </option>
                      {vesselTypes.map(type => <option key={type.value} value={type.value} data-id="element-1405">
                          {type.label}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-1406">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1407">
                      {t('specialists.form.labels.yearsExp')}{' '}
                      <span className="text-red-500" data-id="element-1408">*</span>
                    </label>
                    <input type="number" value={yearsExp} onChange={e => setYearsExp(e.target.value)} placeholder="e.g., 5" min="0" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1409" />
                  </div>
                </div>

                <div data-id="element-1410">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-1411">
                    {t('specialists.form.labels.availability')}{' '}
                    <span className="text-red-500" data-id="element-1412">*</span>
                  </label>
                  <input type="date" value={availability} onChange={e => setAvailability(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-1413" />
                  <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-1414">
                    {t('specialists.form.labels.availabilityHint')}
                  </p>
                </div>

                <div className="pt-4" data-id="element-1415">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1416">
                    <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-[var(--color-tan)] border-gray-300 rounded focus:ring-[var(--color-tan)]" data-id="element-1417" />
                    <span className="text-sm text-[var(--color-text-dark)]" data-id="element-1418">
                      {t('specialists.form.labels.consent')}{' '}
                      <span className="text-red-500" data-id="element-1419">*</span>
                    </span>
                  </label>
                </div>

                {submitStatus === 'success' && <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-id="element-1420">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-1421" />
                    <span className="font-medium" data-id="element-1422">{t('formStatus.sent')}</span>
                  </div>}
                {submitStatus === 'error' && <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-1423">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-1424" />
                    <span data-id="element-1425">{t('formStatus.error')}</span>
                  </div>}

                <div className="flex justify-center pt-2" data-id="element-1426">
                  <button type="submit" disabled={!agreedToTerms || submitting} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-opacity flex items-center gap-2 ${agreedToTerms && !submitting ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-1427">
                    {submitting ? <CheckCircle className="w-5 h-5 animate-spin" data-id="element-1428" /> : <CheckCircle className="w-5 h-5" data-id="element-1429" />}
                    {submitStatus === 'success' ? <span data-id="element-1430">{t('specialists.form.labels.submitSuccess')}</span> : t('specialists.form.labels.submit')}
                  </button>
                </div>

                <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-1431">
                  {t('specialists.form.labels.disclaimer')}
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>

      {/* Contact */}
      <div className="cv-lazy-section-sm" data-id="element-1432">
        <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-dark)]" data-id="element-1433">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1434">
            <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-1435">
              <span className="text-sm font-medium text-white" data-id="element-1436">
                {t('specialists.contact.pill')}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1437">
              {t('specialists.contact.title')}
            </h2>
            <p className="text-lg text-gray-400 mb-8" data-id="element-1438">
              {t('specialists.contact.desc')}
            </p>

            <div className="flex flex-wrap justify-center gap-4" data-id="element-1439">
              <a href="https://t.me/elvora_consulting" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1440">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-1441">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-1442" />
                </svg>
                <span className="text-sm font-medium" data-id="element-1443">Telegram</span>
              </a>
              <a href="https://api.whatsapp.com/send/?phone=380731356233" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1444">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-1445">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" data-id="element-1446" />
                </svg>
                <span className="text-sm font-medium" data-id="element-1447">WhatsApp</span>
              </a>
              <a href="https://www.linkedin.com/company/111293896/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1448">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-1449">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-1450" />
                </svg>
                <span className="text-sm font-medium" data-id="element-1451">LinkedIn</span>
              </a>
              <a href="mailto:info@elvora.consulting" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-1452">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1453">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-1454" />
                </svg>
                <span className="text-sm font-medium" data-id="element-1455">
                  info@elvora.consulting
                </span>
              </a>
            </div>
          </div>
        </section>
      </div>

      <Footer hideSocials data-id="element-1456" />

      <CompanyFormModal isOpen={companyModalOpen} onClose={() => setCompanyModalOpen(false)} data-id="element-1457" />
      <CVFormModal isOpen={cvModalOpen} onClose={() => setCvModalOpen(false)} mode="seafarer" data-id="element-1458" />
    </div>;
}